import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { FaPlay, FaPause, FaChartLine, FaComment, FaFlag } from 'react-icons/fa';
import './SyncedControlBar.css';

/** Fraction along the padded timeline track (matches thumb / trim layer). */
function clientXToTrackFraction(el, clientX) {
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  if (!(rect.width > 0)) return 0;
  return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
}

const SyncedControlBar = ({ 
  currentTime, 
  duration, 
  isPlaying, 
  onPlayPause, 
  onSeek,
  dataOffset = 0,
  onDataOffsetChange,
  comments = [],
  tracks = [],
  /** Absolute data times (telemetry `time`), same space as charts / analysis trim */
  analysisTrim = null,
  onAnalysisTrimChange,
  analysisTrimDisabled = false,
}) => {
  const timelineRef = useRef(null);
  const trackRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  /** Local visual overrides while dragging (props may lag behind). */
  const [dragVisual, setDragVisual] = useState(null);
  const [hoveredComment, setHoveredComment] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const currentTimeRef = useRef(currentTime);
  const onSeekRef = useRef(onSeek);
  const durationRef = useRef(duration);
  const onDataOffsetChangeRef = useRef(onDataOffsetChange);
  const analysisTrimDragRef = useRef({
    analysisTrim: null,
    onAnalysisTrimChange: null,
    dataOffset: 0,
    duration: 0,
  });
  const trimLayerRef = useRef(null);
  const dragCommitRafRef = useRef(null);
  const pendingSyncOffsetRef = useRef(null);
  const pendingTrimRef = useRef(null);
  const activeTrimDragRef = useRef(null);

  // Keep refs up to date
  React.useEffect(() => {
    currentTimeRef.current = currentTime;
    onSeekRef.current = onSeek;
    durationRef.current = duration;
    onDataOffsetChangeRef.current = onDataOffsetChange;
  }, [currentTime, onSeek, duration, onDataOffsetChange]);

  const formatTime = (seconds) => {
    if (seconds == null || isNaN(seconds)) return '0:00';
    const s = Math.abs(seconds);
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    const str = `${mins}:${secs.toString().padStart(2, '0')}`;
    return seconds < 0 ? `-${str}` : str;
  };

  const handleSliderChange = (e) => {
    const newTime = parseFloat(e.target.value);
    const constrainedTime = tracks.length ? Math.max(0, Math.min(newTime, duration || 0)) : Math.max(0, Math.min(newTime, duration || 0));
    onSeek(constrainedTime);
  };

  // Progress fill from start to current time (elapsed = accent bar)
  const safeDuration = duration > 0 ? duration : 0;
  const progressPercent = safeDuration > 0 ? Math.min(100, Math.max(0, ((currentTime || 0) / safeDuration) * 100)) : 0;

  // Marker sticks with playhead; offset shifts it forward/back. Position = currentTime + dataOffset
  const markerPosition = Math.max(0, Math.min(safeDuration, (currentTime || 0) + dataOffset));
  const markerPositionPercent = safeDuration > 0 ? (markerPosition / safeDuration) * 100 : 0;
  const displayMarkerPositionPercent =
    dragVisual?.syncPct != null ? dragVisual.syncPct : markerPositionPercent;

  analysisTrimDragRef.current = {
    analysisTrim,
    onAnalysisTrimChange,
    dataOffset,
    duration: safeDuration,
  };

  const trimVisual = useMemo(() => {
    if (!analysisTrim || !onAnalysisTrimChange || analysisTrimDisabled) return null;
    const lo = Number(analysisTrim.minTime);
    const hi = Number(analysisTrim.maxTime);
    const st = Number(analysisTrim.startTime);
    const en = Number(analysisTrim.endTime);
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo || !(safeDuration > 0)) return null;
    const off = Number(dataOffset) || 0;
    const clampT = (t) => {
      const v = Number(t);
      if (!Number.isFinite(v)) return lo;
      return Math.min(hi, Math.max(lo, v));
    };
    const orderedS = clampT(Math.min(st, en));
    const orderedE = clampT(Math.max(st, en));
    const pct = (dataT) => ((Number(dataT) - off) / safeDuration) * 100;
    return {
      startPct: pct(orderedS),
      endPct: pct(orderedE),
    };
  }, [analysisTrim, onAnalysisTrimChange, analysisTrimDisabled, safeDuration, dataOffset]);

  const displayTrimVisual = useMemo(() => {
    if (!trimVisual) return null;
    if (dragVisual?.trimStartPct != null || dragVisual?.trimEndPct != null) {
      return {
        startPct: dragVisual.trimStartPct ?? trimVisual.startPct,
        endPct: dragVisual.trimEndPct ?? trimVisual.endPct,
      };
    }
    return trimVisual;
  }, [trimVisual, dragVisual]);

  const flushPendingSyncOffset = useCallback(() => {
    dragCommitRafRef.current = null;
    const offset = pendingSyncOffsetRef.current;
    if (offset != null && onDataOffsetChangeRef.current) {
      onDataOffsetChangeRef.current(offset);
    }
    pendingSyncOffsetRef.current = null;
  }, []);

  const flushPendingTrim = useCallback(() => {
    dragCommitRafRef.current = null;
    const next = pendingTrimRef.current;
    if (next && analysisTrimDragRef.current.onAnalysisTrimChange) {
      analysisTrimDragRef.current.onAnalysisTrimChange(next);
    }
    pendingTrimRef.current = null;
  }, []);

  const scheduleSyncOffsetCommit = useCallback(
    (offset) => {
      pendingSyncOffsetRef.current = offset;
      if (dragCommitRafRef.current == null) {
        dragCommitRafRef.current = requestAnimationFrame(flushPendingSyncOffset);
      }
    },
    [flushPendingSyncOffset],
  );

  const scheduleTrimCommit = useCallback(
    (next) => {
      pendingTrimRef.current = next;
      if (dragCommitRafRef.current == null) {
        dragCommitRafRef.current = requestAnimationFrame(flushPendingTrim);
      }
    },
    [flushPendingTrim],
  );

  const beginAnalysisTrimDrag = useCallback(
    (which) => (ev) => {
      if (analysisTrimDisabled) return;
      const tp = analysisTrimDragRef.current;
      if (!tp.onAnalysisTrimChange || !tp.analysisTrim) return;
      const { minTime: lo, maxTime: hi } = tp.analysisTrim;
      if (!Number.isFinite(Number(lo)) || !Number.isFinite(Number(hi)) || !(tp.duration > 0)) return;
      ev.preventDefault();
      ev.stopPropagation();

      const clampData = (t) => {
        const v = Number(t);
        if (!Number.isFinite(v)) return lo;
        return Math.min(hi, Math.max(lo, v));
      };

      const posToDataTime = (clientX) => {
        const el = trimLayerRef.current || trackRef.current;
        const dur = analysisTrimDragRef.current.duration;
        const off = Number(analysisTrimDragRef.current.dataOffset) || 0;
        if (!el || !(dur > 0)) return lo;
        const playbackT = clientXToTrackFraction(el, clientX) * dur;
        return clampData(playbackT + off);
      };

      const posToTrimPct = (clientX) => {
        const el = trimLayerRef.current || trackRef.current;
        const dur = analysisTrimDragRef.current.duration;
        const off = Number(analysisTrimDragRef.current.dataOffset) || 0;
        if (!el || !(dur > 0)) return 0;
        const dataT = clientXToTrackFraction(el, clientX) * dur + off;
        return (dataT - off) / dur * 100;
      };

      const applyTrimAtClientX = (clientX, commitParent) => {
        const tcur = analysisTrimDragRef.current;
        if (!tcur.analysisTrim) return;
        const { minTime: lo2, maxTime: hi2 } = tcur.analysisTrim;
        const off = Number(tcur.dataOffset) || 0;
        const live = activeTrimDragRef.current;
        const orderedS = Number(live?.startTime ?? tcur.analysisTrim.startTime);
        const orderedE = Number(live?.endTime ?? tcur.analysisTrim.endTime);
        const c = posToDataTime(clientX);
        const pct = posToTrimPct(clientX);
        let next;
        let visual;
        if (which === 'start') {
          const e = Number.isFinite(orderedE) ? Math.max(c, orderedE) : hi2;
          next = { startTime: c, endTime: e };
          visual = {
            trimStartPct: pct,
            trimEndPct: ((e - off) / tcur.duration) * 100,
          };
        } else {
          const s = Number.isFinite(orderedS) ? Math.min(orderedS, c) : lo2;
          next = { startTime: s, endTime: c };
          visual = {
            trimStartPct: ((s - off) / tcur.duration) * 100,
            trimEndPct: pct,
          };
        }
        activeTrimDragRef.current = next;
        setDragVisual((prev) => ({ ...prev, ...visual }));
        if (commitParent) {
          scheduleTrimCommit(next);
        }
        return next;
      };

      setIsDragging(true);
      activeTrimDragRef.current = {
        startTime: Number(tp.analysisTrim.startTime),
        endTime: Number(tp.analysisTrim.endTime),
      };
      const startX = ev.touches?.[0]?.clientX ?? ev.clientX;
      applyTrimAtClientX(startX, true);

      const onMove = (ev2) => {
        ev2.preventDefault?.();
        applyTrimAtClientX(ev2.touches?.[0]?.clientX ?? ev2.clientX, true);
      };

      const onUp = (ev2) => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onUp);
        if (dragCommitRafRef.current != null) {
          cancelAnimationFrame(dragCommitRafRef.current);
          dragCommitRafRef.current = null;
        }
        const endX = ev2?.touches?.[0]?.clientX ?? ev2?.clientX;
        const finalNext = endX != null ? applyTrimAtClientX(endX, false) : pendingTrimRef.current;
        pendingTrimRef.current = null;
        if (finalNext && analysisTrimDragRef.current.onAnalysisTrimChange) {
          analysisTrimDragRef.current.onAnalysisTrimChange(finalNext);
        }
        activeTrimDragRef.current = null;
        setIsDragging(false);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onUp);
    },
    [analysisTrimDisabled, scheduleTrimCommit],
  );

  // Click on track (or empty space) to seek to that time
  const handleTrackClick = (e) => {
    if (!timelineRef.current || !onSeekRef.current || !safeDuration) return;
    const trackEl = e.currentTarget;
    const rect = trackEl.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const seekTime = percent * safeDuration;
    onSeekRef.current(seekTime);
  };

  const beginSyncMarkerDrag = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!onDataOffsetChangeRef.current || !(durationRef.current > 0)) return;

      const trackEl = () => trimLayerRef.current || trackRef.current;

      const applyAtClientX = (clientX, commitParent) => {
        const el = trackEl();
        const dur = durationRef.current;
        const frac = clientXToTrackFraction(el, clientX);
        const pct = frac * 100;
        const dragPosition = frac * dur;
        const newOffset = dragPosition - currentTimeRef.current;
        setDragVisual((prev) => ({ ...prev, syncPct: pct }));
        if (commitParent) {
          scheduleSyncOffsetCommit(newOffset);
        }
        return newOffset;
      };

      setIsDragging(true);
      applyAtClientX(e.touches?.[0]?.clientX ?? e.clientX, true);

      const onMove = (ev) => {
        ev.preventDefault?.();
        applyAtClientX(ev.touches?.[0]?.clientX ?? ev.clientX, true);
      };

      const onUp = (ev) => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onUp);
        if (dragCommitRafRef.current != null) {
          cancelAnimationFrame(dragCommitRafRef.current);
          dragCommitRafRef.current = null;
        }
        const endX = ev?.touches?.[0]?.clientX ?? ev?.clientX;
        const finalOffset =
          endX != null ? applyAtClientX(endX, false) : pendingSyncOffsetRef.current;
        pendingSyncOffsetRef.current = null;
        if (finalOffset != null && onDataOffsetChangeRef.current) {
          onDataOffsetChangeRef.current(finalOffset);
        }
        setIsDragging(false);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('touchend', onUp);
    },
    [scheduleSyncOffsetCommit],
  );

  // Drop local overrides once props catch up after drag
  useEffect(() => {
    if (isDragging || !dragVisual) return;
    setDragVisual((prev) => {
      if (!prev) return prev;
      let next = { ...prev };
      if (prev.syncPct != null && Math.abs(prev.syncPct - markerPositionPercent) < 0.15) {
        delete next.syncPct;
      }
      if (
        trimVisual &&
        prev.trimStartPct != null &&
        Math.abs(prev.trimStartPct - trimVisual.startPct) < 0.15
      ) {
        delete next.trimStartPct;
      }
      if (
        trimVisual &&
        prev.trimEndPct != null &&
        Math.abs(prev.trimEndPct - trimVisual.endPct) < 0.15
      ) {
        delete next.trimEndPct;
      }
      if (Object.keys(next).length === 0) return null;
      return next;
    });
  }, [isDragging, dragVisual, markerPositionPercent, trimVisual]);

  return (
    <div className="synced-control-bar">
      <button className="control-btn" onClick={onPlayPause}>
        {isPlaying ? <FaPause /> : <FaPlay />}
      </button>
      
      <div className="timeline-wrapper">
        <span className="time-display">
          {formatTime(currentTime)}
        </span>
        
        <div className={`timeline-track-container${isDragging ? ' timeline-dragging' : ''}`} ref={timelineRef}>
          {tracks.length > 0 && (
            <div className="timeline-multi-track">
              {tracks.map((t, i) => {
                const d = t.duration > 0 ? t.duration : 1;
                const pct = Math.min(100, (100 * (currentTime || 0)) / d);
                return (
                  <div key={i} className="timeline-multi-track-row" style={{ ['--track-color']: t.color || '#888' }}>
                    <div className="timeline-multi-track-fill" style={{ width: `${pct}%` }} />
                  </div>
                );
              })}
            </div>
          )}
          <div className="timeline-track" ref={trackRef} onClick={handleTrackClick}>
          {/* Slider at bottom (z 0): receives clicks/drags */}
          <input
            type="range"
            min="0"
            max={duration || 100}
            step="0.1"
            value={currentTime || 0}
            onChange={handleSliderChange}
            className="timeline-slider"
          />
          {/* Rail on top (z 1): gray bg + accent progress */}
          <div className="timeline-track-rail">
            <div className="timeline-track-bg" aria-hidden />
            <div
              className="timeline-progress"
              style={{ width: `${progressPercent}%` }}
              aria-hidden
            />
          </div>
          {/* Sync marker: sticks with playhead; drag forward/back to offset */}
          {onDataOffsetChange && (
            <div 
              className="offset-marker sync-offset-marker"
              style={{ left: `${displayMarkerPositionPercent}%` }}
              onMouseDown={beginSyncMarkerDrag}
              onTouchStart={beginSyncMarkerDrag}
              title={`Data position: ${formatTime(markerPosition)} (offset ${formatTime(dataOffset)})`}
            >
              <div className="marker-icon">
                <FaChartLine />
              </div>
              <div className="marker-triangle"></div>
            </div>
          )}

          {/* Comment markers */}
          {comments.map((comment) => {
            const commentPercent = duration > 0 ? (comment.timestamp / duration) * 100 : 0;
            return (
              <div
                key={comment.id}
                className="comment-marker"
                style={{ left: `${commentPercent}%` }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSeekRef.current && onSeekRef.current(comment.timestamp);
                }}
                onMouseEnter={(e) => {
                  if (timelineRef.current) {
                    const markerRect = e.currentTarget.getBoundingClientRect();
                    const timelineRect = timelineRef.current.getBoundingClientRect();
                    const relativeX = markerRect.left + markerRect.width / 2 - timelineRect.left;
                    setTooltipPosition({
                      x: relativeX,
                      y: 0
                    });
                  }
                  setHoveredComment(comment);
                }}
                onMouseLeave={() => {
                  setHoveredComment(null);
                }}
              >
                <FaComment />
              </div>
            );
          })}

          {/* Comment tooltip */}
          {hoveredComment && (
            <div 
              className="comment-tooltip"
              style={{
                left: `${tooltipPosition.x}px`,
                bottom: '100%',
                transform: 'translateX(-50%)',
                marginBottom: '8px'
              }}
            >
              <div className="comment-tooltip-header">
                <span className="comment-tooltip-time">{formatTime(hoveredComment.timestamp)}</span>
                {hoveredComment.author && (
                  <span className="comment-tooltip-author">
                    {hoveredComment.author.displayName || hoveredComment.author.email || 'Unknown'}
                  </span>
                )}
              </div>
              <div className="comment-tooltip-text">{hoveredComment.text}</div>
            </div>
          )}

          {/* 3) Custom thumb on top (z 2): visible dot at current position */}
          <div className="timeline-thumb">
            <div
              className="timeline-thumb-dot"
              style={{ left: `${progressPercent}%` }}
            />
          </div>

          {/* Analysis window (absolute data time) mapped onto master playback timeline */}
          {displayTrimVisual && (
            <div
              className="timeline-trim-layer"
              ref={trimLayerRef}
              role="group"
              aria-label="Analysis time range"
            >
              <div
                className="timeline-trim-range"
                style={{
                  left: `${Math.min(displayTrimVisual.startPct, displayTrimVisual.endPct)}%`,
                  width: `${Math.abs(displayTrimVisual.endPct - displayTrimVisual.startPct)}%`,
                }}
              />
              <button
                type="button"
                className="timeline-trim-handle timeline-trim-handle-start"
                style={{ left: `${displayTrimVisual.startPct}%` }}
                aria-label="Analysis window start"
                disabled={analysisTrimDisabled}
                onMouseDown={beginAnalysisTrimDrag('start')}
                onTouchStart={beginAnalysisTrimDrag('start')}
              >
                <FaFlag aria-hidden />
              </button>
              <button
                type="button"
                className="timeline-trim-handle timeline-trim-handle-end"
                style={{ left: `${displayTrimVisual.endPct}%` }}
                aria-label="Analysis window end"
                disabled={analysisTrimDisabled}
                onMouseDown={beginAnalysisTrimDrag('end')}
                onTouchStart={beginAnalysisTrimDrag('end')}
              >
                <FaFlag aria-hidden />
              </button>
            </div>
          )}
          </div>
        </div>
        
        <span className="time-display">
          {formatTime(duration)}
        </span>
      </div>
    </div>
  );
};

export default SyncedControlBar;

